import React from 'react';
import { Button, Input, Space, Card, Typography, Checkbox, Tooltip } from 'antd';
import { PlusOutlined, DeleteOutlined, LockOutlined } from '@ant-design/icons';

const { Text } = Typography;

export interface KeyValueItem {
  id?: number;
  keyName: string;
  keyValue: string;
  isSensitive: boolean;
}

interface KeyValueInputProps {
  value?: KeyValueItem[];
  onChange?: (value: KeyValueItem[]) => void;
  disabled?: boolean;
  placeholder?: {
    key?: string;
    value?: string;
  };
}

export const KeyValueInput: React.FC<KeyValueInputProps> = ({
  value = [],
  onChange,
  disabled = false,
  placeholder = { key: 'Key (e.g., Username, URL)', value: 'Value' },
}) => {
  const handleAdd = () => {
    const newItems = [...value, { keyName: '', keyValue: '', isSensitive: false }];
    onChange?.(newItems);
  };

  const handleRemove = (index: number) => {
    const newItems = value.filter((_, i) => i !== index);
    onChange?.(newItems);
  };

  const handleChange = (index: number, field: keyof KeyValueItem, newValue: string | boolean) => {
    const newItems = [...value];
    newItems[index] = { ...newItems[index], [field]: newValue };
    onChange?.(newItems);
  };

  return (
    <Card
      title={
        <Space>
          <span>Custom Requirements</span>
          <Text type="secondary" style={{ fontWeight: 'normal', fontSize: 12 }}>
            (Add credentials, URLs, configuration items, etc.)
          </Text>
        </Space>
      }
      size="small"
      extra={
        !disabled && (
          <Button type="dashed" onClick={handleAdd} icon={<PlusOutlined />} size="small">
            Add Item
          </Button>
        )
      }
    >
      {value.length === 0 ? (
        <Text type="secondary">
          No custom requirements added. Click "Add Item" to add key-value pairs.
        </Text>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {value.map((item, index) => (
            <div
              key={index}
              style={{
                display: 'flex',
                gap: 8,
                alignItems: 'flex-start',
                padding: 12,
                background: item.isSensitive ? '#fff7e6' : '#fafafa',
                borderRadius: 4,
                border: item.isSensitive ? '1px solid #ffd591' : '1px solid #d9d9d9',
              }}
            >
              <div style={{ flex: 1 }}>
                <Input
                  placeholder={placeholder.key}
                  value={item.keyName}
                  onChange={(e) => handleChange(index, 'keyName', e.target.value)}
                  disabled={disabled}
                  style={{ marginBottom: 8 }}
                  addonBefore="Key"
                />
                <Input.TextArea
                  placeholder={placeholder.value}
                  value={item.keyValue}
                  onChange={(e) => handleChange(index, 'keyValue', e.target.value)}
                  disabled={disabled}
                  rows={2}
                  style={{ marginBottom: 8 }}
                />
                <Tooltip title="Mark as sensitive to protect this value (e.g., passwords, API keys)">
                  <Checkbox
                    checked={item.isSensitive}
                    onChange={(e) => handleChange(index, 'isSensitive', e.target.checked)}
                    disabled={disabled}
                  >
                    <Space size={4}>
                      <LockOutlined />
                      <span>Sensitive data</span>
                    </Space>
                  </Checkbox>
                </Tooltip>
              </div>
              {!disabled && (
                <Button
                  type="text"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={() => handleRemove(index)}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </Card>
  );
};
